/**
 * Google Analytics 4 (gtag) — same measurement ID as legacy qbcon.in website.
 * Source: qbcon.in_version8.2_S3 website footer / Frontend header → G-LLKRN56D63
 */

declare global {
  interface Window {
    /** Shared with mediaEvents / GTM — keep optional + loose typing */
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim()

let initialized = false
let lastPath = ''

export function getGaMeasurementId(): string {
  return GA_ID
}

export function initGoogleAnalytics(): void {
  if (!GA_ID || typeof window === 'undefined' || initialized) return
  initialized = true

  window.dataLayer = window.dataLayer || []
  // gtag expects the Arguments object, not a plain array
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }

  window.gtag('js', new Date())
  // SPA: we send page_view on route changes ourselves.
  window.gtag('config', GA_ID, { send_page_view: false })

  const script = document.createElement('script')
  script.id = 'ga-gtag'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`
  document.head.appendChild(script)
}

/** Track a SPA page view (pathname + search). */
export function trackPageview(path: string, title?: string): void {
  if (!GA_ID || typeof window === 'undefined') return
  if (!initialized) initGoogleAnalytics()
  if (!window.gtag) return

  const pagePath = path || '/'
  if (pagePath === lastPath) return
  lastPath = pagePath

  safeGtag('event', 'page_view', {
    page_path: pagePath,
    page_title: title || document.title,
    page_location: window.location.href,
  })
}

/** Analytics must never break the calling UI action (blocked/stubbed gtag can throw). */
export function safeGtag(...args: unknown[]): void {
  try {
    window.gtag?.(...args)
  } catch {
    // ignore
  }
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>,
): void {
  if (!GA_ID || typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', eventName, params)
}
